// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
// Results step for the contact data upload workflow.
// Displays created, updated, not-updated, and failed contacts with expandable sections.
import { Link } from '@/components/link';
import {
  Add as AddIcon,
  CheckCircle as CheckCircleIcon,
  CheckRounded,
  ContentCopyRounded,
  Error as ErrorIcon,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
  Info as InfoIcon,
} from '@/constants/hardfin-standard-icons';
import { Contact } from '@/contact/api/types';
import { ContactDataUploadResult } from '@/contact/api/upload-contact-data';
import type { ExpandedContactsState } from '@/contact/components/contact-data-upload/types';
import { formatNumber } from '@/utilities/number';
import { pluralize } from '@/utilities/pluralize';
import {
  createIdentifierSuffix,
  formatFailedRowsForCopy,
} from '@/utilities/upload-helpers';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Card,
  CardContent,
  Collapse,
  IconButton,
  ListItem,
  ListItemText,
  Paper,
  Tooltip,
  Typography,
} from '@mui/material';
import { useState } from 'react';
import { FixedSizeList as VirtualList } from 'react-window';

const ITEM_HEIGHT = 40;
const FAILED_ROW_HEIGHT = 80;

interface Props {
  uploadResult: ContactDataUploadResult | null;
  expandedContacts: ExpandedContactsState;
  onToggleExpanded: (section: keyof ExpandedContactsState) => void;
  onBack: () => void;
  onStartNew: () => void;
  onClose: () => void;
}

interface RowFailure {
  rowNumber: number;
  contactName: string;
  errors: string[];
}

interface ContactRowProps {
  index: number;
  style: React.CSSProperties;
  data: Contact[];
}

const ContactRow = (props: ContactRowProps) => {
  const contact = props.data[props.index];
  return (
    <div style={props.style}>
      <ListItem component="div" dense>
        <ListItemText
          primary={
            <Link
              to={`/contacts/$contactKey`}
              params={{ contactKey: contact.key }}
            >
              {contact.firstName} {contact.lastName}
            </Link>
          }
        />
      </ListItem>
    </div>
  );
};

interface FailedRowItemProps {
  index: number;
  style: React.CSSProperties;
  data: RowFailure[];
}

const FailedRowItem = (props: FailedRowItemProps) => {
  const row = props.data[props.index];
  return (
    <div style={props.style}>
      <ListItem component="div" dense>
        <ListItemText
          primary={`For file row ${row.rowNumber}${row.contactName ? ` (contact "${row.contactName}")` : ''}:`}
          secondary={
            <ul style={{ margin: 0, paddingLeft: '16px' }}>
              {row.errors.map((error, idx) => (
                <li key={idx}>{error}</li>
              ))}
            </ul>
          }
          secondaryTypographyProps={{ component: 'div' }}
        />
      </ListItem>
    </div>
  );
};

/**
 * Displays the results of a contact data upload, including created, updated,
 * not-updated, and failed contacts in expandable sections.
 *
 * @param props.uploadResult - The result of the upload operation
 * @param props.expandedContacts - State tracking which result sections are expanded
 * @param props.onToggleExpanded - Callback to toggle a result section
 * @param props.onBack - Callback to go back to the upload step
 * @param props.onStartNew - Callback to start a new upload
 * @param props.onClose - Callback to close the dialog
 */
export function Results(props: Props) {
  const [isCopied, setIsCopied] = useState(false);

  if (!props.uploadResult) {
    return (
      <Card>
        <CardContent>
          <Typography color="error">
            No upload results available. Please try again.
          </Typography>
          <Button variant="contained" onClick={props.onStartNew} sx={{ mt: 2 }}>
            Start new upload
          </Button>
        </CardContent>
      </Card>
    );
  }

  const { success, items, structureErrors, rowErrors } = props.uploadResult;

  const contactsCreated: Contact[] = [];
  const contactsUpdated: Contact[] = [];
  const contactsNotUpdated: Contact[] = [];
  const failedRows: RowFailure[] = [];

  /**
   * Handles copying all failed row errors to clipboard with visual feedback
   */
  const handleCopyErrors = async (e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      const text = formatFailedRowsForCopy(failedRows, (row) =>
        createIdentifierSuffix(row, 'contactName', 'contact')
      );
      await navigator.clipboard.writeText(text);
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 2000);
    } catch (error) {
      console.error('Failed to copy errors:', error);
    }
  };

  rowErrors.forEach((row) => {
    if (row.errors.length > 0) {
      failedRows.push({
        rowNumber: row.index,
        contactName: '',
        errors: row.errors.map(
          (err) => `Column "${err.fieldName}" has an error: ${err.error}`
        ),
      });
      return;
    }
  });

  items.forEach((item) => {
    if (item.errors.length > 0) {
      failedRows.push({
        rowNumber: item.index,
        contactName: item.contactName,
        errors: item.errors.map(
          (err) => `Column "${err.fieldName}" has an error: ${err.error}`
        ),
      });
      return;
    }
    if (item.noOp && item.validatedContact) {
      contactsNotUpdated.push(item.validatedContact);
      return;
    }
    if (item.created && item.validatedContact) {
      contactsCreated.push(item.validatedContact);
      return;
    }
    if (item.updated && item.validatedContact) {
      contactsUpdated.push(item.validatedContact);
    }
  });

  const totalProcessed =
    contactsCreated.length + contactsUpdated.length + contactsNotUpdated.length;

  return (
    <Card>
      <CardContent>
        {success ? (
          <>
            <Alert severity="success" sx={{ mb: 3 }}>
              <AlertTitle>Success</AlertTitle>
              <Typography variant="body2">
                Your contact data uploaded successfully for{' '}
                {pluralize(totalProcessed, {
                  singular: 'contact',
                  plural: 'contacts',
                })}
              </Typography>
            </Alert>

            {contactsCreated.length > 0 && (
              <Paper variant="outlined" sx={{ mb: 2, overflow: 'hidden' }}>
                <Box
                  sx={{
                    p: 2,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    bgcolor: 'success.light',
                    color: 'success.contrastText',
                    cursor: contactsCreated.length ? 'pointer' : undefined,
                  }}
                  onClick={
                    contactsCreated.length
                      ? () => props.onToggleExpanded('created')
                      : undefined
                  }
                >
                  <Box display="flex" alignItems="center">
                    <AddIcon sx={{ mr: 1 }} />
                    <Typography variant="subtitle1">
                      Contacts created ({formatNumber(contactsCreated.length)})
                    </Typography>
                  </Box>
                  {contactsCreated.length > 0 &&
                    (props.expandedContacts.created ? (
                      <ExpandLessIcon />
                    ) : (
                      <ExpandMoreIcon />
                    ))}
                </Box>
                <Collapse in={props.expandedContacts.created}>
                  <VirtualList
                    height={Math.min(contactsCreated.length * ITEM_HEIGHT, 200)}
                    itemCount={contactsCreated.length}
                    itemSize={ITEM_HEIGHT}
                    itemData={contactsCreated}
                    width="100%"
                  >
                    {ContactRow}
                  </VirtualList>
                </Collapse>
              </Paper>
            )}

            {contactsUpdated.length > 0 && (
              <Paper variant="outlined" sx={{ mb: 2, overflow: 'hidden' }}>
                <Box
                  sx={{
                    p: 2,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    bgcolor: 'success.light',
                    color: 'success.contrastText',
                    cursor: contactsUpdated.length ? 'pointer' : undefined,
                  }}
                  onClick={
                    contactsUpdated.length
                      ? () => props.onToggleExpanded('updated')
                      : undefined
                  }
                >
                  <Box display="flex" alignItems="center">
                    <CheckCircleIcon sx={{ mr: 1 }} />
                    <Typography variant="subtitle1">
                      Contacts updated ({formatNumber(contactsUpdated.length)})
                    </Typography>
                  </Box>
                  {contactsUpdated.length > 0 &&
                    (props.expandedContacts.updated ? (
                      <ExpandLessIcon />
                    ) : (
                      <ExpandMoreIcon />
                    ))}
                </Box>
                <Collapse in={props.expandedContacts.updated}>
                  <VirtualList
                    height={Math.min(contactsUpdated.length * ITEM_HEIGHT, 200)}
                    itemCount={contactsUpdated.length}
                    itemSize={ITEM_HEIGHT}
                    itemData={contactsUpdated}
                    width="100%"
                  >
                    {ContactRow}
                  </VirtualList>
                </Collapse>
              </Paper>
            )}

            {contactsNotUpdated.length > 0 && (
              <Paper variant="outlined" sx={{ mb: 2, overflow: 'hidden' }}>
                <Box
                  sx={{
                    p: 2,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    bgcolor: (theme) => theme.palette.grey.A400,
                    color: 'info.contrastText',
                    cursor: contactsNotUpdated.length ? 'pointer' : undefined,
                  }}
                  onClick={
                    contactsNotUpdated.length
                      ? () => props.onToggleExpanded('notUpdated')
                      : undefined
                  }
                >
                  <Box display="flex" alignItems="center">
                    <InfoIcon sx={{ mr: 1 }} />
                    <Typography variant="subtitle1">
                      Contacts with no updates (
                      {formatNumber(contactsNotUpdated.length)})
                    </Typography>
                  </Box>
                  {contactsNotUpdated.length > 0 &&
                    (props.expandedContacts.notUpdated ? (
                      <ExpandLessIcon />
                    ) : (
                      <ExpandMoreIcon />
                    ))}
                </Box>
                <Collapse in={props.expandedContacts.notUpdated}>
                  <VirtualList
                    height={Math.min(
                      contactsNotUpdated.length * ITEM_HEIGHT,
                      200
                    )}
                    itemCount={contactsNotUpdated.length}
                    itemSize={ITEM_HEIGHT}
                    itemData={contactsNotUpdated}
                    width="100%"
                  >
                    {ContactRow}
                  </VirtualList>
                </Collapse>
              </Paper>
            )}
          </>
        ) : (
          <>
            <Alert severity="error" sx={{ mb: 3 }}>
              {structureErrors.length > 0 && (
                <>
                  <AlertTitle>File format invalid</AlertTitle>
                  <Typography variant="body2">
                    The uploaded file has column names that do not match the
                    required contact data fields
                  </Typography>
                </>
              )}
              {failedRows.length > 0 && (
                <>
                  <AlertTitle>Uploaded data invalid</AlertTitle>
                  <Typography variant="body2">
                    The uploaded file includes data that does not match your
                    contacts or field requirements
                  </Typography>
                </>
              )}
            </Alert>
            {structureErrors && structureErrors.length > 0 && (
              <Paper variant="outlined" sx={{ mb: 2, overflow: 'hidden' }}>
                <Box
                  sx={{
                    p: 2,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    bgcolor: 'error.light',
                    color: 'error.contrastText',
                    cursor: 'pointer',
                  }}
                  onClick={() => props.onToggleExpanded('structureErrors')}
                >
                  <Box display="flex" alignItems="center">
                    <ErrorIcon sx={{ mr: 1 }} />
                    <Typography variant="subtitle1">
                      Mismatched columns ({formatNumber(structureErrors.length)}
                      )
                    </Typography>
                  </Box>
                  {props.expandedContacts.structureErrors ? (
                    <ExpandLessIcon />
                  ) : (
                    <ExpandMoreIcon />
                  )}
                </Box>
                <Collapse in={props.expandedContacts.structureErrors}>
                  <ul
                    style={{
                      maxHeight: '300px',
                      overflow: 'auto',
                      listStyleType: 'disc',
                    }}
                  >
                    {structureErrors.map((row) => (
                      <li
                        key={`structure-${row.fieldName}`}
                        style={{ paddingBottom: '8px' }}
                      >
                        Column &quot;{`${row.fieldName}`}&quot; not found
                      </li>
                    ))}
                  </ul>
                </Collapse>
              </Paper>
            )}
            {failedRows && failedRows.length > 0 && (
              <Paper variant="outlined" sx={{ mb: 2, overflow: 'hidden' }}>
                <Box
                  sx={{
                    p: 2,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    bgcolor: 'error.light',
                    color: 'error.contrastText',
                    cursor: 'pointer',
                  }}
                  onClick={() => props.onToggleExpanded('failed')}
                >
                  <Box display="flex" alignItems="center">
                    <ErrorIcon sx={{ mr: 1 }} />
                    <Typography variant="subtitle1">
                      Invalid rows ({formatNumber(failedRows.length)}{' '}
                      {pluralize(failedRows.length, {
                        includeCount: false,
                        singular: 'contact',
                        plural: 'contacts',
                      })}
                      )
                    </Typography>
                  </Box>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Tooltip
                      title={isCopied ? 'Copied!' : 'Copy all errors'}
                      arrow
                    >
                      <IconButton
                        size="small"
                        onClick={handleCopyErrors}
                        aria-label={isCopied ? 'Copied' : 'Copy all errors'}
                        sx={{
                          color: 'error.contrastText',
                          '&:hover': {
                            bgcolor: 'error.dark',
                          },
                        }}
                      >
                        {isCopied ? (
                          <CheckRounded fontSize="small" />
                        ) : (
                          <ContentCopyRounded fontSize="small" />
                        )}
                      </IconButton>
                    </Tooltip>
                    {props.expandedContacts.failed ? (
                      <ExpandLessIcon />
                    ) : (
                      <ExpandMoreIcon />
                    )}
                  </Box>
                </Box>
                <Collapse in={props.expandedContacts.failed}>
                  <VirtualList
                    height={Math.min(
                      failedRows.length * FAILED_ROW_HEIGHT,
                      300
                    )}
                    itemCount={failedRows.length}
                    itemSize={FAILED_ROW_HEIGHT}
                    itemData={failedRows}
                    width="100%"
                  >
                    {FailedRowItem}
                  </VirtualList>
                </Collapse>
              </Paper>
            )}
          </>
        )}

        <Box display="flex" justifyContent="space-between" mt={3}>
          <Button variant="outlined" onClick={props.onBack}>
            Back
          </Button>
          <Box>
            <Button
              variant="outlined"
              onClick={props.onStartNew}
              sx={{ mr: 1 }}
            >
              Start new upload
            </Button>
            <Button variant="contained" onClick={props.onClose}>
              Close
            </Button>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
}
